import React from 'react';
import { Page } from '@react-pdf/renderer';
import { getNumber } from 'js/components/number/number';
import { getImpressionThreshold } from 'js/utils';
import { pageSizes } from '../constants';
import Aside from '../components/aside/aside';
import AnnotatedChart from '../components/annotated-chart/annotated-chart';
import Logos from '../components/logos/logos';
import PageContent from '../components/page-content/page-content';
import Groups from '../components/groups/groups';
import NoDataAlert from '../components/no-data-alert/no-data-alert';
import styles from '../report.styles';
import { getCompletionRate, getVideoReportLogoRecommendations } from '../utils';

export default function VideoLogoThemesPage({
    orientation,
    logo4D,
    brandLogo,
    charts,
    report,
}) {
    const impressionThreshold = getImpressionThreshold(report.impressions);
    const isPortrait = orientation === 'portrait';
    const pageClasses = [
        styles.page,
        isPortrait ? styles.pagePortrait : styles.pageLandscape,
    ];

    if (logo4D || brandLogo) {
        pageClasses.push(isPortrait ? null : styles.pageLandscapeWithLogo);
    }

    const recommendations = getVideoReportLogoRecommendations(report);

    const formatGroups = (logoGroups) =>
        [...logoGroups].map((i) => ({
            icon: i.logobase64 || null,
            label: i.logo_group_name,
            impressions: getNumber({
                value: i.impressions,
                maximumFractionDigits: 0,
            }),
            starts: getNumber({
                value: i.starts,
                maximumFractionDigits: 0,
            }),
            completionRate:
                getCompletionRate(report) > 0 &&
                getNumber({
                    value: getCompletionRate(i),
                    isPercentage: true,
                    maximumFractionDigits: 2,
                }),
            trueViewRate:
                getCompletionRate(report) > 0 &&
                getNumber({
                    value: getCompletionRate(i),
                    isPercentage: true,
                    maximumFractionDigits: 2,
                }),
        }));

    const topGroupsByStartViews = [...report.logo_groups]
        .sort((a, b) => b.starts - a.starts)
        .slice(0, 3)
        .filter((item) => item.impressions >= impressionThreshold);

    const topGroupsByCompletionRate = [...report.logo_groups]
        .sort((a, b) => getCompletionRate(b) - getCompletionRate(a))
        .slice(0, 3)
        .filter((item) => item.impressions >= impressionThreshold);

    const showImpChart = report.logo_groups.some(
        (group) => group.impressions >= impressionThreshold,
    );

    const impChart = showImpChart && (
        <AnnotatedChart
            orientation={orientation}
            annotations={[{ title: 'Top Brand Groups by Impressions' }]}
            chart={charts.LOGO_GROUPS}
        />
    );

    const startViewsGroups = topGroupsByStartViews.length > 0 && (
        <Groups
            orientation={orientation}
            title="Top Brand Groups by Started Views"
            groups={formatGroups(topGroupsByStartViews)}
            showGroupTitles
            background="#1CBEC9"
            parameters={['impressions', 'starts', 'completionRate']}
            mainParameter="starts"
            hasBorderTop
        />
    );

    const completionRateGroups = getCompletionRate(report) > 0 &&
        topGroupsByCompletionRate.length > 0 && (
            <Groups
                orientation={orientation}
                title="Top Brand Groups by Completion Rate"
                groups={formatGroups(topGroupsByCompletionRate)}
                showGroupTitles
                background="#4C9AFF"
                parameters={['impressions', 'starts', 'completionRate']}
                mainParameter="completionRate"
                hasBorderTop
            />
        );

    return (
        <Page
            size={isPortrait ? pageSizes.portrait : pageSizes.landscape}
            orientation={orientation}
            style={pageClasses}
        >
            <Aside
                orientation={orientation}
                title="Brand Themes"
                description="Brand Themes are combinations of brands that share a common overarching theme. Use these insights to understand how you can improve your performance by targeting relevant brands."
                hasLogo={Boolean(logo4D || brandLogo)}
                recommendations={recommendations}
            />
            <PageContent orientation={orientation}>
                {impChart || startViewsGroups || completionRateGroups ? (
                    <>
                        {impChart}
                        {startViewsGroups}
                        {completionRateGroups}
                    </>
                ) : (
                    <NoDataAlert />
                )}
            </PageContent>
            <Logos
                orientation={orientation}
                logo4D={logo4D}
                brandLogo={brandLogo}
            />
        </Page>
    );
}
